import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { artistsApi } from '../../repositories/artists'
import { adminApi } from '../../repositories/admin'
import { soundchartsEndpoints } from '../../repositories/soundcharts'
import { playlistsApi } from '../../repositories/playlists'
import { UsersIcon, PhotoIcon, HeartIcon, ChatBubbleLeftIcon, PlayCircleIcon, ChartBarIcon, UserGroupIcon, EyeIcon } from '@heroicons/react/24/outline'
import InfoTooltip from '../../components/common/InfoTooltip'
import Pagination from '../../components/common/Pagination'
import { playlistCountLabel } from '../../components/playlists/playlistCountLabel'
import { PlaylistFreshness } from '../../components/playlists/PlaylistFreshness'
import { EntryDateCell, ExitDateCell } from '../../components/playlists/PlaylistDateCells'
import type { PlaylistMeta } from '../../repositories/playlists'
import PageHeader from '../../components/ui/PageHeader'
import StatCard from '../../components/ui/StatCard'
import Avatar from '../../components/ui/Avatar'
import DataTable, { DataTableColumn } from '../../components/ui/DataTable'
import { Skeleton, SkeletonCard, SkeletonStatCards, SkeletonTableRows } from '../../components/ui/skeleton'
import { HOME_CRUMB } from '../../components/ui/Breadcrumbs'
import { useAppSelector } from '../../hooks/redux'

interface GenderState {
    code: 'MALE' | 'FEMALE';
    weight: number;
}
interface CountriesState {
    countryName: string;
    weight: number;
}
interface NoteableUser {
    imageUrl: string;
    username: string;
    followerCount: number;
    engagementCount: number;
    countryName: string;
    fullName?: string;
}
interface InterstState {
    name: string;
    weight: number;
    affinity: number;
}
interface Lookalike {
    username: string;
    followerCount: number;
}
interface InstagramStats {
    followerCount: number
    postCount: number
    viewCount: number
    engagementCount: number
    engagementRate: number
    averageLikesPerPost: number
    averageCommentsPerPost: number
    averageReelsPlays: number
    averageViewsPerPost: number
    genders: GenderState[];
    country: CountriesState[];
    noteableUsers: NoteableUser[];
    interests: InterstState[];
}

interface TikTokStats {
    followerCount: number
    postCount: number
    viewCount: number
    engagementCount: number
    engagementRate: number
    averageLikesPerPost: number
    averageCommentsPerPost: number
    averageViewsPerPost: number
    genders: GenderState[];
    country: CountriesState[];
    noteableUsers: NoteableUser[];
    lookalikes: Lookalike[]
}

interface YouTubeStats {
    followerCount: number
    postCount: number
    viewCount: number
    engagementCount: number
    engagementRate: number
    averageLikesPerPost: number
    averageCommentsPerPost: number
    averageViewsPerPost: number
    genders: GenderState[];
    country: CountriesState[];
    noteableUsers: NoteableUser[];
    lookalikes: Lookalike[]
}

interface TwitterStats {
    followerCount: number
    likeCount: number
}

/** Titled panel sitting directly on top of a DataTable, as on /dashboard. */
const TablePanel = ({
    title,
    children,
}: {
    title: string;
    children: React.ReactNode;
}) => (
    // break-inside-avoid keeps a panel whole inside the multi-column layout,
    // which packs the panels tightly instead of leaving a grid-row gap under
    // the shorter table.
    <div className="mb-4 break-inside-avoid">
        <div className="px-4 py-3 bg-white border rounded-t-lg border-neutral-200">
            <h2 className="text-xs font-semibold tracking-wide uppercase text-primary-900">
                {title}
            </h2>
        </div>
        {children}
    </div>
);

const countryColumns: DataTableColumn<CountriesState>[] = [
    { key: 'country', header: 'Country', cell: (c) => c.countryName },
    {
        key: 'followers',
        header: 'Followers',
        cell: (c) => `${(c.weight * 100).toFixed(1)}%`,
    },
];

const notableUserColumns: DataTableColumn<NoteableUser>[] = [
    {
        key: 'profile',
        header: 'Profile',
        // Person rows, so they get the shared avatar treatment.
        cell: (u) => <Avatar src={u.imageUrl} name={u.fullName || u.username} />,
    },
    {
        key: 'name',
        header: 'Name',
        cell: (u) => <span className="font-medium">{u.username}</span>,
    },
    {
        key: 'followers',
        header: 'Follower Count',
        cell: (u) => u.followerCount.toLocaleString(),
    },
    {
        key: 'engagement',
        header: 'Engagement Count',
        cell: (u) => u.engagementCount.toLocaleString(),
    },
    { key: 'country', header: 'Country Name', cell: (u) => u.countryName },
];

const interestColumns: DataTableColumn<InterstState>[] = [
    { key: 'name', header: 'Interest Name', cell: (i) => i.name },
    {
        key: 'weight',
        header: 'Weight',
        cell: (i) => `${(i.weight * 100).toFixed(0)}%`,
    },
    { key: 'affinity', header: 'Affinity Level', cell: (i) => i.affinity },
];

const lookalikeColumns: DataTableColumn<Lookalike>[] = [
    {
        key: 'username',
        header: 'Username',
        cell: (l) => <span className="font-medium">{l.username}</span>,
    },
    {
        key: 'followers',
        header: 'Follower Count',
        cell: (l) => l.followerCount.toLocaleString(),
    },
];

const ArtistAnalytics: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { user } = useAppSelector((state) => state.auth);
    const [artist, setArtist] = useState<any>(null)
    const [loading, setLoading] = useState(false)
    const [popularity, setPopularity] = useState<number | null>(null)
    const [retentionRate, setRetentionRate] = useState<{
        conversionRate: number
        followers: number
        listeners: number
    } | null>(null)
    const [instagramStats, setInstagramStats] = useState<InstagramStats | null>(null)
    const [tiktokStats, setTiktokStats] = useState<TikTokStats | null>(null);
    const [youtubeStats, setYoutubeStats] = useState<YouTubeStats | null>(null);
    const [twitterStats, setTwitterStats] = useState<TwitterStats | null>(null);
    const [popularityLoading, setPopularityLoading] = useState(false);
    const [selectedPlatform, setSelectedPlatform] = useState<'instagram' | 'tiktok' | 'youtube' | 'twitter'>('instagram');
    const [playlists, setPlaylists] = useState<any[]>([]);
    const [playlistsLoading, setPlaylistsLoading] = useState(false);
    const [playlistScope, setPlaylistScope] = useState<1 | 0>(1);
    const [playlistPlatform, setPlaylistPlatform] = useState<string>('spotify');
    const [playlistPage, setPlaylistPage] = useState(1);
    // Count of real placements after deduplication, reported by the backend.
    const [playlistTotal, setPlaylistTotal] = useState(0);
    // True when the backend's page cap stopped its walk early, so the set below is incomplete.
    const [playlistsCapped, setPlaylistsCapped] = useState(false);
    // The market the result is scoped to. The backend applies one of its own on Apple Music and
    // Amazon, where a playlist is published once per market. Null means every market.
    const [playlistCountry, setPlaylistCountry] = useState<string | null>(null);
    // Rows to page through. Stored data lists stale placements without counting them, so paging
    // by the count would leave the last of them unreachable.
    const [playlistPageTotal, setPlaylistPageTotal] = useState(0);
    // Where the data came from and how fresh it is; drives the "Data as of" line. This view has
    // no Refresh button: it is the DSP users' view, and a refresh spends SoundCharts quota.
    const [playlistMeta, setPlaylistMeta] = useState<PlaylistMeta | null>(null);
    const [playlistSearch, setPlaylistSearch] = useState('');
    const [playlistSort, setPlaylistSort] = useState<{ col: 'song' | 'peakPosition' | null; dir: 'asc' | 'desc' }>({ col: null, dir: 'asc' });
    // A display page size. The backend fetches the artist's complete set and deduplicates across
    // all of it before paging.
    const PLAYLIST_PAGE_SIZE = 10;
    const PLAYLIST_PLATFORMS = ['spotify', 'apple-music', 'deezer', 'amazon', 'tidal', 'youtube', 'audiomack', 'soundcloud'];

    const platformDisplayNames: { [key: string]: string } = {
        spotify: "Spotify",
        amazon: "Amazon Music",
        deezer: "Deezer",
        tidal: "TIDAL",
        "apple-music": "Apple Music",
        youtube: "YouTube",
        audiomack: "Audiomack",
        soundcloud: "SoundCloud",
    };
    useEffect(() => {
        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

        const fetchArtist = async () => {
            if (!id) return

            if (UUID_REGEX.test(id)) {
                const response = await soundchartsEndpoints.getArtistByUUID(id)
                const artistData = response.data.object

                setArtist({...artistData, artistUUID: artistData.uuid})
                fetchArtistMetrics(artistData.uuid)
                return
            }

            try {
                setLoading(true)
                const response = user?.role === 'dsp'
                    ? await adminApi.getDspArtist(id)
                    : await artistsApi.getOne(id)
                const artistData = response.data.data || response.data
                setArtist(artistData)
                if (artistData?.artistUUID) {
                    fetchArtistMetrics(artistData.artistUUID)
                }
            } catch (error) {
                console.error('Error fetching artist:', error)
            } finally {
                setLoading(false)
            }
        }
        fetchArtist()
    }, [id, user?.role])

    useEffect(() => {
        setPlaylistPage(1);
        setPlaylistSearch('');
        setPlaylistSort({ col: null, dir: 'asc' });
    }, [playlistScope, playlistPlatform, artist?.artistUUID])

    useEffect(() => {
        if (user?.role !== 'dsp' || !artist?.artistUUID) return;
        const artistUUID = artist.artistUUID;

        const fetchPlaylists = async () => {
            setPlaylistsLoading(true);
            try {
                const res = await playlistsApi.getPlaylists(
                    'artist',
                    artistUUID,
                    playlistPlatform,
                    {
                        currentOnly: playlistScope,
                        page: playlistPage,
                        limit: PLAYLIST_PAGE_SIZE,
                    }
                );
                const items = (res?.data?.data || []).map((item: any) => ({ ...item, _platform: playlistPlatform }));
                const meta = res?.data?.meta;
                const filtered = playlistScope === 0 ? items.filter((p: any) => p.exitDate) : items;
                setPlaylists(filtered);
                setPlaylistTotal(meta?.totalPlaylists ?? 0);
                setPlaylistPageTotal(res?.data?.pagination?.totalItems ?? meta?.totalPlaylists ?? 0);
                setPlaylistsCapped(Boolean(meta?.capped));
                setPlaylistCountry(meta?.countryCode ?? null);
                setPlaylistMeta(meta ?? null);
            } catch (e) {
                setPlaylists([]);
                setPlaylistTotal(0);
                setPlaylistPageTotal(0);
                setPlaylistsCapped(false);
                setPlaylistCountry(null);
                setPlaylistMeta(null);
            } finally {
                setPlaylistsLoading(false);
            }
        };
        fetchPlaylists();
    }, [user?.role, artist?.artistUUID, playlistScope, playlistPlatform, playlistPage])

    const fetchArtistMetrics = async (artistUUID: string) => {
        setPopularityLoading(true);

        const isDsp = user?.role === 'dsp';

        // Calculate dates
        const today = new Date();
        const currentDate = today.toISOString().split('T')[0];
        const previousDate = new Date(today);
        previousDate.setDate(today.getDate() - 1);
        const previousFormattedDate = previousDate.toISOString().split('T')[0];

        // Popularity
        try {
            const popularityRes = await soundchartsEndpoints.getPopularityByArtist(
                artistUUID,
                'spotify',
                `startDate=${previousFormattedDate}`
            );
            const items = popularityRes?.data?.items || [];
            const latestPopularity = items.length > 0 ? items[items.length - 1] : null;
            setPopularity(latestPopularity?.value ?? 0);
        } catch (error) {
            console.error('Error fetching popularity:', error);
            setPopularity(0);
        }

        if (!isDsp) {

            // Retention Rate (not shown for DSP)
            try {
                const retentionRes = await soundchartsEndpoints.getFanRetentionByArtist(
                    artistUUID,
                    'spotify',
                    `startDate=${currentDate}`
                );
                const retentionItems = retentionRes?.data?.items || [];
                const latestRetention = retentionItems.length > 0 ? retentionItems[0] : null;
                if (latestRetention) {
                    setRetentionRate({
                        conversionRate: latestRetention.conversionRate,
                        followers: latestRetention.followers,
                        listeners: latestRetention.listeners,
                    });
                } else {
                    setRetentionRate(null);
                }
            } catch (error) {
                console.error('Error fetching retention rate:', error);
                setRetentionRate(null);
            }
        }

        // Instagram Audience Report
        try {
            const audienceReportRes = await soundchartsEndpoints.getAudienceReportLatest(artistUUID, 'instagram');
            const autdienceData = audienceReportRes?.data?.object;
            const instagramStatesData = autdienceData?.audience?.stats;
            console.log('Instagram Audience Report Response:', audienceReportRes);
            const genders = autdienceData?.audience?.audienceLike?.genders || [];
            const counteries = autdienceData?.audience?.audienceLike?.countries || [];
            const noteableuser = autdienceData?.audience?.audienceLike?.notableUsers || [];
            const interests = autdienceData?.audience?.audienceLike?.interests || [];

            if (autdienceData) {
                setInstagramStats({
                    followerCount: instagramStatesData.followerCount,
                    postCount: instagramStatesData.postCount,
                    viewCount: instagramStatesData.viewCount,
                    engagementCount: instagramStatesData.engagementCount,
                    engagementRate: instagramStatesData.engagementRate,
                    averageLikesPerPost: instagramStatesData.averageLikesPerPost,
                    averageCommentsPerPost: instagramStatesData.averageCommentsPerPost,
                    averageReelsPlays: instagramStatesData.averageReelsPlays,
                    averageViewsPerPost: instagramStatesData.averageViewsPerPost,
                    genders: genders,
                    country: counteries,
                    noteableUsers: noteableuser,
                    interests: interests,
                });
            } else {
                setInstagramStats(null);
            }
        } catch (error) {
            console.error('Error fetching Instagram audience report:', error);
            setInstagramStats(null);
        }
        //Tiktok Audience Report
        try {
            const audienceReportRes = await soundchartsEndpoints.getAudienceReportLatest(artistUUID, 'tiktok');
            console.log('TikTok Audience Report Response:', audienceReportRes);
            const autdienceData = audienceReportRes?.data?.object;
            const tiktokStatesData = autdienceData?.audience?.stats;
            const genders = autdienceData?.audience?.audienceFollower?.genders || [];
            const counteries = autdienceData?.audience?.audienceFollower?.countries || [];
            const noteableuser = autdienceData?.audience?.audienceFollower?.notableUsers || [];
            const lookalike = autdienceData?.audience?.audienceFollower?.lookalikes || [];
            if (autdienceData) {
                setTiktokStats({
                    followerCount: tiktokStatesData.followerCount,
                    postCount: tiktokStatesData.postCount,
                    viewCount: tiktokStatesData.viewCount,
                    engagementCount: tiktokStatesData.engagementCount,
                    engagementRate: tiktokStatesData.engagementRate,
                    averageLikesPerPost: tiktokStatesData.averageLikesPerPost,
                    averageCommentsPerPost: tiktokStatesData.averageCommentsPerPost,
                    averageViewsPerPost: tiktokStatesData.averageViewsPerPost,
                    genders: genders,
                    country: counteries,
                    noteableUsers: noteableuser,
                    lookalikes: lookalike,
                });
            } else {
                setTiktokStats(null);
            }
        } catch (error) {
            console.error('Error fetching TikTok audience report:', error);
            setTiktokStats(null);
        }

        //Yoututbe Audience Report (not shown for DSP)
        if (!isDsp) {
            try {
                const audienceReportRes = await soundchartsEndpoints.getAudienceReportLatest(artistUUID, 'youtube');
                console.log('youtubeeeeeee Audience Report Response:', audienceReportRes);
                const autdienceData = audienceReportRes?.data?.object;
                const tiktokStatesData = autdienceData?.audience?.stats;
                const genders = autdienceData?.audience?.audienceFollower?.genders || [];
                const counteries = autdienceData?.audience?.audienceFollower?.countries || [];
                const noteableuser = autdienceData?.audience?.audienceFollower?.notableUsers || [];
                const lookalike = autdienceData?.audience?.audienceFollower?.lookalikes || [];
                console.log('TikTok Lookalikes Data:=>>>>>>>>>', lookalike);
                if (autdienceData) {
                    setYoutubeStats({
                        followerCount: tiktokStatesData.followerCount,
                        postCount: tiktokStatesData.postCount,
                        viewCount: tiktokStatesData.viewCount,
                        engagementCount: tiktokStatesData.engagementCount,
                        engagementRate: tiktokStatesData.engagementRate,
                        averageLikesPerPost: tiktokStatesData.averageLikesPerPost,
                        averageCommentsPerPost: tiktokStatesData.averageCommentsPerPost,
                        averageViewsPerPost: tiktokStatesData.averageViewsPerPost,
                        genders: genders,
                        country: counteries,
                        noteableUsers: noteableuser,
                        lookalikes: lookalike,
                    });
                } else {
                    setYoutubeStats(null);
                }
            } catch (error) {
                console.error('Error fetching YouTube audience report:', error);
                setYoutubeStats(null);
            }
        }

        //Twitter Audience Report (not shown for DSP)
        if (!isDsp) {
            try {
                const audienceReportRes = await soundchartsEndpoints.getAudience(artistUUID, 'twitter', `startDate=${previousFormattedDate}`);
                console.log('twitter  Audience Report Response:', audienceReportRes);
                const twitterStatsData = audienceReportRes?.data?.items[0];
                // console.log('Twitter Audience Data:=>>>>>>>>>', twitterStatsData[0]);

                if (twitterStatsData) {
                    setTwitterStats({
                        followerCount: twitterStatsData.followerCount,
                        likeCount: twitterStatsData.likeCount,
                    });
                } else {
                    setTwitterStats(null);
                }
            } catch (error) {
                console.error('Error fetching YouTube audience report:', error);
                setTwitterStats(null);
            }
        }

        setPopularityLoading(false);
    }

    const formatGenderStats = (genders: GenderState[]) => {
        const male = genders.find(g => g.code === 'MALE');
        const female = genders.find(g => g.code === 'FEMALE');
        const malePercent = male ? (male.weight * 100).toFixed(0) : '0';
        const femalePercent = female ? (female.weight * 100).toFixed(0) : '0';
        return `Male ${malePercent}% & Female ${femalePercent}%`;
    };
    
    return (
        <div className='py-2 sm:p-6'>
            {/* This page is only reached from the Analytics > Artist tab. */}
            <PageHeader
                breadcrumbs={[
                    HOME_CRUMB,
                    { label: 'Analytics', to: '/analytics?tab=artist' },
                    { label: 'Artist Analytics' },
                ]}
                className='mb-6'
                title='Artist Analytics'
            />

            {loading ? (
                <div className='space-y-6' aria-busy='true'>
                    <SkeletonStatCards count={2} className='sm:grid-cols-2' />
                    <Skeleton className='h-8 w-72' />
                    <SkeletonStatCards count={4} className='grid-cols-2 sm:grid-cols-2 md:grid-cols-4' />
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                        <SkeletonCard lines={6} />
                        <SkeletonCard lines={6} />
                    </div>
                </div>
            ) : artist ? (
                user?.role === 'dsp' ? (
                    <div className='space-y-6'>
                        {/* Artist name and popularity score */}
                        <div className='bg-white rounded-xl p-5 border border-neutral-100'>
                            <h2 className='text-2xl font-bold text-black'>
                                {artist?.name || artist?.artistName || '—'}
                            </h2>
                            <div className='mt-1 flex items-center gap-2'>
                                <span className='text-sm text-neutral-500'>Popularity:</span>
                                <span className='text-sm font-semibold text-black'>
                                    {popularityLoading ? <Skeleton className='inline-block w-8 h-4 align-middle' /> : popularity != null ? popularity : 'N/A'}
                                </span>
                            </div>
                        </div>

                        {/* DSP-only view: IG + TikTok follower counts and artist playlists */}
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            <StatCard
                                icon={<UsersIcon className="w-5 h-5" />}
                                label="Instagram Followers"
                                loading={popularityLoading}
                                value={
                                    instagramStats?.followerCount != null
                                            ? instagramStats.followerCount.toLocaleString()
                                            : 'N/A'
                                }
                            />
                            <StatCard
                                icon={<UsersIcon className="w-5 h-5" />}
                                label="TikTok Followers"
                                loading={popularityLoading}
                                value={
                                    tiktokStats?.followerCount != null
                                            ? tiktokStats.followerCount.toLocaleString()
                                            : 'N/A'
                                }
                            />
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <h2 className="mb-4 text-xl font-semibold text-black">
                                    Playlist Analytics by Platform
                                </h2>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-4">
                                {PLAYLIST_PLATFORMS.map((platform) => (
                                    <button
                                    key={platform}
                                    onClick={() => setPlaylistPlatform(platform)}
                                    className={`px-4 py-2 text-sm font-bold rounded-lg transition-colors ${playlistPlatform === platform
                                        ? "bg-primary-700 text-white"
                                        : "bg-neutral-100 text-neutral-700 hover:bg-neutral-200"
                                        }`}
                                    >
                                    {platformDisplayNames[platform] || platform}
                                    </button>
                                ))}
                            </div>

                            <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2">
                                    <input
                                        type="text"
                                        placeholder="Search playlist names"
                                        value={playlistSearch}
                                        onChange={(e) => setPlaylistSearch(e.target.value)}
                                        className="px-3 py-2 text-sm bg-white border rounded-lg border-neutral-300 text-black placeholder-neutral-400 focus:outline-none focus:border-primary-500"
                                    />
                                </div>
                            </div>
                            
                            <div className="flex gap-4 mb-4">
                                <button
                                    onClick={() => setPlaylistScope(1)}
                                    className={`px-4 py-2 text-sm font-semibold rounded ${playlistScope === 1 ? "bg-primary-700 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                                    }`}
                                >
                                    Current Data
                                </button>
                                <button
                                    onClick={() => setPlaylistScope(0)}
                                    className={`px-4 py-2 text-sm font-semibold rounded ${playlistScope === 0 ? "bg-primary-700 text-white" : "bg-neutral-100 text-neutral-700 hover:bg-primary-50 hover:text-primary-700"
                                    }`}
                                >
                                    Historical Data
                                </button>
                            </div>
                            
                            <div className="flex items-center justify-between w-full mb-4">
                                <h3 className="text-lg font-semibold text-black">
                                    Playlists on {platformDisplayNames[playlistPlatform]}
                                </h3>
                            </div>

                            {/* A count of real placements, deduplicated on the backend across the
                                artist's complete result set, with when SoundCharts collected them. */}
                            {playlistsLoading ? (
                                <Skeleton className='w-32 h-4 mb-2' />
                            ) : (
                                <PlaylistFreshness
                                    countLabel={playlistCountLabel(playlistTotal, playlistsCapped, playlistCountry)}
                                    meta={playlistMeta}
                                />
                            )}

                            <div className='overflow-x-auto bg-white border border-neutral-200 rounded-lg'>
                                <table className='min-w-full'>
                                    <thead>
                                        <tr className='bg-neutral-100 border-b border-neutral-200'>
                                            <th className='px-4 py-3 text-sm font-semibold text-left text-neutral-700'>Playlist</th>
                                            <th className='px-4 py-3 text-sm font-semibold text-left text-neutral-700'>
                                                <button
                                                    onClick={() => setPlaylistSort(s => s.col === 'song' ? { col: 'song', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: 'song', dir: 'asc' })}
                                                    className='flex items-center gap-1 hover:text-primary-700'
                                                >
                                                    Song Title
                                                    <span className='text-xs'>{playlistSort.col === 'song' ? (playlistSort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                                                </button>
                                            </th>
                                            <th className='px-4 py-3 text-sm font-semibold text-left text-neutral-700'>
                                                <button
                                                    onClick={() => setPlaylistSort(s => s.col === 'peakPosition' ? { col: 'peakPosition', dir: s.dir === 'asc' ? 'desc' : 'asc' } : { col: 'peakPosition', dir: 'asc' })}
                                                    className='flex items-center gap-1 hover:text-primary-700'
                                                >
                                                    Peak Position
                                                    <span className='text-xs'>{playlistSort.col === 'peakPosition' ? (playlistSort.dir === 'asc' ? '▲' : '▼') : '⇅'}</span>
                                                </button>
                                            </th>
                                            <th className='px-4 py-3 text-sm font-semibold text-left text-neutral-700'>Entry Date</th>
                                            {playlistScope === 0 && (
                                                <th className='px-4 py-3 text-sm font-semibold text-left text-neutral-700'>Exit Date</th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {playlistsLoading ? (
                                            <SkeletonTableRows rows={5} columns={playlistScope === 0 ? 5 : 4} />
                                        ) : playlists.length === 0 ? (
                                            <tr><td colSpan={playlistScope === 0 ? 4 : 3} className='px-4 py-6 text-center text-neutral-500'>
                                                {playlistMeta?.syncStatus === 'never_synced'
                                                    ? 'No playlist data collected yet.'
                                                    : playlistMeta?.countryCovered === false
                                                    ? 'No stored playlist data for this country.'
                                                    : playlistScope === 1 ? 'Artist is not currently on any playlists for this platform.' : 'No historical playlist placements found for this platform.'}
                                            </td></tr>
                                        ) : (() => {
                                            // Deduplication happens on the backend across the artist's complete
                                            // result set, so only search runs here.
                                            const term = playlistSearch.trim().toLowerCase();
                                            const filtered = term
                                                ? playlists.filter((item) =>
                                                    (item.playlist?.name || '').toLowerCase().includes(term)
                                                )
                                                : [...playlists];
                                            if (playlistSort.col) {
                                                const dir = playlistSort.dir === 'asc' ? 1 : -1;
                                                filtered.sort((a, b) => {
                                                    if (playlistSort.col === 'song') {
                                                        return dir * (a.song?.name || '').localeCompare(b.song?.name || '');
                                                    }
                                                    return dir * ((a.peakPosition ?? Infinity) - (b.peakPosition ?? Infinity));
                                                });
                                            }
                                            return filtered.length === 0
                                                ? <tr><td colSpan={playlistScope === 0 ? 4 : 3} className='px-4 py-6 text-center text-neutral-500'>No playlists match your search.</td></tr>
                                                : filtered.map((item, idx) => (
                                                <tr key={`${playlistScope}-${item.playlist?.uuid || idx}-${idx}`} className='border-b border-neutral-100 hover:bg-neutral-50'>
                                                    {/* <td className='px-4 py-3 text-sm text-black'>{item.playlist?.name || '—'}</td> */}
                                                    <td className="px-4 py-3">
                                                        <div className="flex items-center gap-3">
                                                            <img
                                                                src={item.playlist.imageUrl}
                                                                alt={item.playlist.name}
                                                                className="object-cover bg-neutral-100 rounded-md shadow-sm w-14 h-14"
                                                                onError={(e) =>
                                                                ((
                                                                    e.target as HTMLImageElement
                                                                ).style.display = "none")
                                                                }
                                                            />
                                                            <span className="max-w-xs text-sm text-black truncate" title={item.playlist.name}>
                                                                {item.playlist.name}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td className='px-4 py-3 text-sm text-neutral-700 capitalize'>{item.song.name}</td>
                                                    <td className='px-4 py-3 text-sm text-neutral-700 capitalize'>{item.peakPosition}</td>
                                                    <td className='px-4 py-3 text-sm text-neutral-700'>
                                                        <EntryDateCell item={item} formatDate={(value) => new Date(value).toLocaleDateString()} />
                                                    </td>
                                                    {playlistScope === 0 && (
                                                        <td className='px-4 py-3 text-sm text-neutral-700'>
                                                            <ExitDateCell item={item} formatDate={(value) => new Date(value).toLocaleDateString()} />
                                                        </td>
                                                    )}
                                                </tr>
                                            ));
                                        })()}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                page={playlistPage}
                                totalPages={Math.ceil(playlistPageTotal / PLAYLIST_PAGE_SIZE) || 1}
                                setPage={setPlaylistPage}
                                totalItems={playlistPageTotal}
                                limit={PLAYLIST_PAGE_SIZE}
                            />
                            
                        </div>
                    </div>
                ) : (
                <div className='space-y-6'>
                    {/* Artist Info Grid - 2 columns */}
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                        {/* Artist Popularity Score */}
                        <div className='bg-white rounded-lg shadow-sm p-4 border border-neutral-200'>

                            <div className='flex items-center gap-2'>
                                <span className='text-sm font-medium text-neutral-600'>Artist Popularity Score:</span>
                                <InfoTooltip content="
                                     Artist popularity metric indicates how popular an artist is on Spotify" />
                            </div>

                            {popularityLoading ? (
                                <Skeleton className='w-12 h-6 mt-1' />
                            ) : popularity !== null && popularity > 0 ? (
                                <div className='text-lg font-bold text-black'>{popularity}</div>
                            ) : (
                                <div className='text-neutral-400 flex items-center gap-2'>
                                    Not Available
                                </div>
                            )}
                        </div>

                        {/* Followers to Listeners Ratio */}
                        <div className='bg-white rounded-lg shadow-sm p-4 border border-neutral-200'>
                            <div className='flex items-center gap-2 mb-1'>
                                <span className='text-sm font-medium text-neutral-600'>Followers to Listeners Ratio</span>
                                <InfoTooltip content="This metric shows the relationship between the listeners and the followers. Do take into account that not all followers are listeners. We calculate this by dividing the total number of listeners by the total number of followers, multiplied by 100. The higher the percentage is, the better the artist can turn its listeners into followers." />
                            </div>

                            {popularityLoading ? (
                                <div className='grid grid-cols-2 gap-2'>
                                    {[0, 1].map((i) => (
                                        <div key={i} className='space-y-1.5'>
                                            <Skeleton className='w-16 h-3' />
                                            <Skeleton className='w-20 h-6' />
                                        </div>
                                    ))}
                                </div>
                            ) : retentionRate !== null ? (
                                <div className='space-y-3'>
                                    <div className='grid grid-cols-2 gap-2  border-neutral-100'>
                                        <div>
                                            <div className='text-xs text-neutral-500'>Followers</div>
                                            <div className='text-lg font-semibold text-black'>
                                                {retentionRate.followers.toLocaleString()}
                                            </div>
                                        </div>
                                        <div>
                                            <div className='text-xs text-neutral-500'>Listeners</div>
                                            <div className='text-lg font-semibold text-black'>
                                                {retentionRate.listeners.toLocaleString()}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className='text-neutral-400 flex items-center gap-2'>
                                    Not Available
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Platform Tabs */}
                    <div className='inline-flex gap-1 bg-neutral-100 p-1 rounded-lg'>
                        <button
                            onClick={() => setSelectedPlatform('instagram')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedPlatform === 'instagram'
                                ? 'bg-primary-700 text-white'
                                : 'text-neutral-600 hover:text-primary-700'
                                }`}
                        >
                            Instagram
                        </button>
                        <button
                            onClick={() => setSelectedPlatform('tiktok')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedPlatform === 'tiktok'
                                ? 'bg-primary-700 text-white'
                                : 'text-neutral-600 hover:text-primary-700'
                                }`}
                        >
                            TikTok
                        </button>
                        <button
                            onClick={() => setSelectedPlatform('youtube')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedPlatform === 'youtube'
                                ? 'bg-primary-700 text-white'
                                : 'text-neutral-600 hover:text-primary-700'
                                }`}
                        >
                            YouTube
                        </button>
                        <button
                            onClick={() => setSelectedPlatform('twitter')}
                            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${selectedPlatform === 'twitter'
                                ? 'bg-primary-700 text-white'
                                : 'text-neutral-600 hover:text-primary-700'
                                }`}
                        >
                            Twitter
                        </button>
                    </div>

                    {/* Instagram tab */}
                    {selectedPlatform === 'instagram' && (
                        <div>
                            {/* Instagram stats */}
                            {popularityLoading ? (
                                <SkeletonStatCards count={8} className='grid-cols-2 sm:grid-cols-2 md:grid-cols-4' />
                            ) : instagramStats !== null ? (
                                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                                    <StatCard
                                            icon={<UsersIcon className="w-5 h-5" />}
                                            label="Followers"
                                            value={<>{instagramStats.followerCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PhotoIcon className="w-5 h-5" />}
                                            label="Posts"
                                            value={<>{instagramStats.postCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChartBarIcon className="w-5 h-5" />}
                                            label="Engagement Rate"
                                            value={<>{(instagramStats.engagementRate * 100).toFixed(2)}%</>}
                                        />
                                    <StatCard
                                            icon={<HeartIcon className="w-5 h-5" />}
                                            label="Avg Likes/post"
                                            value={<>{instagramStats.averageLikesPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChatBubbleLeftIcon className="w-5 h-5" />}
                                            label="Avg Comments/post"
                                            value={<>{instagramStats.averageCommentsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PlayCircleIcon className="w-5 h-5" />}
                                            label="Avg Reels Plays"
                                            value={<>{instagramStats.averageReelsPlays.toLocaleString()}</>}
                                        />
                                     <StatCard
                                            icon={<EyeIcon className="w-5 h-5" />}
                                            label="Average Views/post"
                                            value={<>{instagramStats.averageViewsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<UserGroupIcon className="w-5 h-5" />}
                                            label="Gender Split"
                                            value={<>{formatGenderStats(instagramStats.genders)}</>}
                                        />
                                </div>
                            ) : (
                                <div className='text-neutral-400 flex items-center justify-center h-32'>
                                    Not Available
                                </div>
                            )}
                            {/* --- Instagram Analytics Tables Section --- */}
                            <div className='columns-1 md:columns-2 gap-4 mt-6'>
                                {/* Top 10 Countries Table */}
                                <TablePanel title="Top 10 Countries">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={countryColumns}
                                            rows={instagramStats?.country?.slice(0, 10) || []}
                                            getRowKey={(c: any) => c.countryName}
                                            emptyMessage="No country data available."
                                        />
                                    </TablePanel>

                                {/* Top 10 Notable Users Table */}
                                <TablePanel title="Top 10 Notable Users">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={notableUserColumns}
                                            rows={instagramStats?.noteableUsers?.slice(0, 15) || []}
                                            getRowKey={(u: any) => u.username}
                                            emptyMessage="No notable user data available."
                                        />
                                    </TablePanel>

                                {/* Top 10 Intrest Table */}

                                <TablePanel title="Top 15 Interests of Followers">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={interestColumns}
                                            rows={instagramStats?.interests?.slice(0, 15) || []}
                                            getRowKey={(i: any) => i.name}
                                            emptyMessage="No interest data available."
                                        />
                                    </TablePanel>

                            </div>
                        </div>
                    )}

                    {/* TikTok tab */}
                    {selectedPlatform === 'tiktok' && (
                        <div>
                            {/* TikTok stats */}
                            {popularityLoading ? (
                                <SkeletonStatCards count={8} className='grid-cols-2 sm:grid-cols-2 md:grid-cols-4' />
                            ) : tiktokStats !== null ? (
                                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                                    <StatCard
                                            icon={<UsersIcon className="w-5 h-5" />}
                                            label="Followers"
                                            value={<>{tiktokStats.followerCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PhotoIcon className="w-5 h-5" />}
                                            label="Posts"
                                            value={<>{tiktokStats.postCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<EyeIcon className="w-5 h-5" />}
                                            label="Total Views"
                                            value={<>{tiktokStats.viewCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChartBarIcon className="w-5 h-5" />}
                                            label="Engagement Rate"
                                            value={<>{(tiktokStats.engagementRate * 100).toFixed(2)}%</>}
                                        />
                                    <StatCard
                                            icon={<HeartIcon className="w-5 h-5" />}
                                            label="Avg Likes/Post"
                                            value={<>{tiktokStats.averageLikesPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChatBubbleLeftIcon className="w-5 h-5" />}
                                            label="Avg Comments/Post"
                                            value={<>{tiktokStats.averageCommentsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PlayCircleIcon className="w-5 h-5" />}
                                            label="Avg Views/Post"
                                            value={<>{tiktokStats.averageViewsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<UserGroupIcon className="w-5 h-5" />}
                                            label="Gender Split"
                                            value={<>{formatGenderStats(tiktokStats.genders)}</>}
                                        />
                                </div>
                            ) : (
                                <div className='text-neutral-400 flex items-center justify-center h-32'>
                                    Not Available
                                </div>
                            )}
                            {/* --- tiktok Analytics Tables Section --- */}
                            <div className='columns-1 md:columns-2 gap-4 mt-6'>
                                {/* Top 10 Countries Table */}
                                <TablePanel title="Top 10 Countries">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={countryColumns}
                                            rows={tiktokStats?.country?.slice(0, 10) || []}
                                            getRowKey={(c: any) => c.countryName}
                                            emptyMessage="No country data available."
                                        />
                                    </TablePanel>

                                {/* Top 10 Notable Users Table */}
                                <TablePanel title="Top 10 Notable Users">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={notableUserColumns}
                                            rows={tiktokStats?.noteableUsers?.slice(0, 15) || []}
                                            getRowKey={(u: any) => u.username}
                                            emptyMessage="No notable user data available."
                                        />
                                    </TablePanel>

                                {/* Top 10 Intrest Table */}

                                <TablePanel title="Top 10 Lookalike Accounts">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={lookalikeColumns}
                                            rows={tiktokStats?.lookalikes?.slice(0, 10) || []}
                                            getRowKey={(l: any) => l.username}
                                            emptyMessage="No lookalike data available."
                                        />
                                    </TablePanel>

                            </div>
                        </div>
                    )}

                    {/* YouTube tab */}
                    {selectedPlatform === 'youtube' && (
                        <div>
                            {/* YouTube stats */}
                            {popularityLoading ? (
                                <SkeletonStatCards count={8} className='grid-cols-2 sm:grid-cols-2 md:grid-cols-4' />
                            ) : youtubeStats !== null ? (
                                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                                    <StatCard
                                            icon={<UsersIcon className="w-5 h-5" />}
                                            label="Followers"
                                            value={<>{youtubeStats.followerCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PhotoIcon className="w-5 h-5" />}
                                            label="Total Posts"
                                            value={<>{youtubeStats.postCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<EyeIcon className="w-5 h-5" />}
                                            label="Total Views"
                                            value={<>{youtubeStats.viewCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChartBarIcon className="w-5 h-5" />}
                                            label="Engagement Rate"
                                            value={<>{(youtubeStats.engagementRate * 100).toFixed(2)}%</>}
                                        />
                                    <StatCard
                                            icon={<HeartIcon className="w-5 h-5" />}
                                            label="Avg Likes/Video"
                                            value={<>{youtubeStats.averageLikesPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<ChatBubbleLeftIcon className="w-5 h-5" />}
                                            label="Avg Comments/Video"
                                            value={<>{youtubeStats.averageCommentsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<PlayCircleIcon className="w-5 h-5" />}
                                            label="Avg Views/post"
                                            value={<>{youtubeStats.averageViewsPerPost.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<UserGroupIcon className="w-5 h-5" />}
                                            label="Gender Split"
                                            value={<>{formatGenderStats(youtubeStats.genders)}</>}
                                        />
                                </div>
                            ) : (
                                <div className='text-neutral-400 flex items-center justify-center h-32'>
                                    Not Available
                                </div>
                            )}
                            {/* --- Yoututbe Analytics Tables Section --- */}
                            <div className='columns-1 md:columns-2 gap-4 mt-6'>
                                {/* Top 10 Countries Table */}
                                <TablePanel title="Top 10 Countries">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={countryColumns}
                                            rows={youtubeStats?.country?.slice(0, 10) || []}
                                            getRowKey={(c: any) => c.countryName}
                                            emptyMessage="No country data available."
                                        />
                                    </TablePanel>

                                {/* Top 10 Notable Users Table */}
                                <TablePanel title="Top 15 Notable Users">
                                        <DataTable
                                            className="rounded-t-none"
                                            loading={popularityLoading}
                                            columns={notableUserColumns}
                                            rows={youtubeStats?.noteableUsers?.slice(0, 15) || []}
                                            getRowKey={(u: any) => u.username}
                                            emptyMessage="No notable user data available."
                                        />
                                    </TablePanel>

                            </div>
                        </div>
                    )}

                    {/* Twitter tab */}
                    {selectedPlatform === 'twitter' && (
                        <div>
                            {/* Twitter stats */}
                            {popularityLoading ? (
                                <SkeletonStatCards count={8} className='grid-cols-2 sm:grid-cols-2 md:grid-cols-4' />
                            ) : twitterStats !== null ? (
                                <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
                                    <StatCard
                                            icon={<UsersIcon className="w-5 h-5" />}
                                            label="Followers"
                                            value={<>{twitterStats.followerCount.toLocaleString()}</>}
                                        />
                                    <StatCard
                                            icon={<HeartIcon className="w-5 h-5" />}
                                            label="Likes"
                                            value={<>{twitterStats.likeCount.toLocaleString()}</>}
                                        />
                                </div>
                            ) : (
                                <div className='text-neutral-400 flex items-center justify-center h-32'>
                                    Not Available
                                </div>
                            )}
                        </div>
                    )}

                </div>
                )
            ) : (
                <div className='flex justify-center items-center h-64'>
                    <div className='text-neutral-500'>No artist data found</div>
                </div>
            )}
        </div>
    )
}

export default ArtistAnalytics
